import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, Check, Brain, GraduationCap, Briefcase, Banknote, User, ShieldCheck, Languages, Loader2 } from "lucide-react";
import { isValidAadhaar, formatAadhaar } from "@/lib/aadhaar";
import { useI18n } from "@/lib/i18n";

const STEPS = [
  { id: 1, labelKey: "step.personal", icon: User },
  { id: 2, labelKey: "step.education", icon: GraduationCap },
  { id: 3, labelKey: "step.interests", icon: Brain },
  { id: 4, labelKey: "step.career", icon: Briefcase },
  { id: 5, labelKey: "step.bank", icon: Banknote },
];

const DOMAINS = [
  "Artificial Intelligence", "Machine Learning", "Data Science", "Web Development",
  "Mobile App Development", "Digital Marketing", "Graphic Design", "Cybersecurity",
  "Cloud Computing", "Tally / Accounting", "Spoken English", "Agriculture Tech",
  "Food Processing", "Tailoring & Fashion", "Beauty & Wellness", "Electrician / Plumbing",
];

const SKILLS = ["MS Office", "Tally", "Photoshop", "Excel", "Coding", "Driving", "Cooking", "Embroidery"];
const LANGUAGES = ["Telugu", "English", "Hindi", "Tamil"];

const HANDICRAFTS = [
  "Wood Carving",
  "Crochet / Lace Knitting",
  "Fishnet Knitted Works",
  "Bamboo Crafts",
  "Brass Crafts",
  "Clay Craft / Pottery",
  "Kalamkari Art",
  "Kondapalli Toys",
  "Etikoppaka Lacquered Toys",
  "Soft Toy Making",
  "Embroidery / Zari Work",
  "Cane & Basket Weaving",
];

const SELF_EMPLOYMENT = [
  "Masonry Works",
  "Boat Making",
  "Rod Bending / Steel Fixing",
  "Painting (Building / Sign)",
  "Driving — LMV (with licensing)",
  "Driving — HMV (with licensing)",
  "Welding & Fabrication",
  "Electrical Wiring",
  "Plumbing",
  "Carpentry",
  "Mobile / TV Repair",
  "Beauty Parlour / Salon",
];

export default function RegisterYouth() {
  const navigate = useNavigate();
  const { lang, setLang, t } = useI18n();
  const [step, setStep] = useState(1);
  const [villages, setVillages] = useState([]);
  const [coursesCatalog, setCoursesCatalog] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [aadhaarStatus, setAadhaarStatus] = useState("idle"); // idle | sending | otp | verifying | verified
  const [demoOtp, setDemoOtp] = useState("");
  const [otp, setOtp] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    aadhaar_number: "",
    gender: "Male",
    date_of_birth: "",
    mobile: "",
    email: "",
    village: "",
    address: "",
    pincode: "",
    education_level: "",
    education_status: "Completed",
    qualification_details: "",
    employment_status: "Unemployed",
    family_income: "",
    interested_domains: [],
    ai_upskill_interest: true,
    placement_preference: "Both",
    job_type_preference: "",
    startup_idea: "",
    bank_linkage_required: false,
    bank_loan_amount: "",
    skills_known: [],
    languages_known: [],
    selected_courses: [],
    interested_handicrafts: [],
    self_employment_interests: [],
    existing_skill_details: "",
    desired_upskill_with_experience: "",
  });

  useEffect(() => {
    api.get("/villages").then(({ data }) => setVillages(data)).catch(() => {});
    api.get("/courses").then(({ data }) => setCoursesCatalog(data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setForm((p) => ({
    ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v]
  }));

  const validateStep = () => {
    if (step === 1) {
      if (!form.full_name || !form.aadhaar_number || !form.mobile || !form.village || !form.date_of_birth) {
        toast.error("Please fill all personal details");
        return false;
      }
      if (!/^\d{12}$/.test(form.aadhaar_number.replace(/\s/g, ""))) {
        toast.error("Aadhaar must be 12 digits");
        return false;
      }
      if (!isValidAadhaar(form.aadhaar_number)) {
        toast.error("Invalid Aadhaar (checksum failed)");
        return false;
      }
      if (!/^\d{10}$/.test(form.mobile)) {
        toast.error("Mobile must be 10 digits");
        return false;
      }
    }
    if (step === 2 && !form.education_level) {
      toast.error("Select education level");
      return false;
    }
    if (step === 3 && form.interested_domains.length === 0) {
      toast.error("Select at least one domain");
      return false;
    }
    return true;
  };

  const sendAadhaarOtp = async () => {
    if (!isValidAadhaar(form.aadhaar_number)) {
      toast.error(t("aadhaar.invalid"));
      return;
    }
    if (!/^\d{10}$/.test(form.mobile)) {
      toast.error("Enter valid 10-digit mobile first");
      return;
    }
    setAadhaarStatus("sending");
    try {
      const { data } = await api.post("/aadhaar/start", {
        aadhaar_number: form.aadhaar_number.replace(/\s/g, ""),
        mobile: form.mobile,
      });
      setDemoOtp(data.demo_otp || "");
      setAadhaarStatus("otp");
      if (data.demo_otp) {
        toast.success(`OTP sent (demo: ${data.demo_otp})`);
      } else {
        toast.success("OTP sent to your mobile");
      }
    } catch (e) {
      setAadhaarStatus("idle");
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    }
  };

  const verifyAadhaarOtp = async () => {
    setAadhaarStatus("verifying");
    try {
      await api.post("/aadhaar/verify", {
        aadhaar_number: form.aadhaar_number.replace(/\s/g, ""),
        otp: otp,
      });
      setAadhaarStatus("verified");
      toast.success(t("aadhaar.verified"));
    } catch (e) {
      setAadhaarStatus("otp");
      toast.error(e?.response?.data?.detail || "OTP failed");
    }
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(5, s + 1)); };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/youth/register", {
        ...form,
        aadhaar_number: form.aadhaar_number.replace(/\s/g, ""),
      });
      toast.success("Registration successful!");
      navigate(`/register/success/${data.registration_no}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCourses = useMemo(() => {
    const s = courseFilter.toLowerCase();
    return coursesCatalog
      .filter((c) => {
        if (!courseFilter) return true;
        return (
          c.course_name.toLowerCase().includes(s) ||
          c.ssc_code.toLowerCase().includes(s) ||
          c.ssc_name.toLowerCase().includes(s) ||
          c.ai_integration.toLowerCase().includes(s)
        );
      })
      .slice(0, 60);
  }, [coursesCatalog, courseFilter]);

  const progress = (step / 5) * 100;

  return (
    <div className="min-h-screen bg-paper">
      <div className="h-1.5 gradient-stripe" />
      <header className="bg-white/80 backdrop-blur-xl border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#1E3A8A] flex items-center justify-center text-white font-heading font-bold">PS</div>
            <div>
              <div className="font-heading font-bold text-lg">padaskillinfo</div>
              <div className="text-xs text-gray-500 -mt-0.5">{t("form.title")}</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-gray-100 rounded-full p-1" data-testid="lang-toggle">
              <button onClick={() => setLang("en")} className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${lang === "en" ? "bg-white shadow text-[#1E3A8A]" : "text-gray-500"}`} data-testid="lang-en">EN</button>
              <button onClick={() => setLang("te")} className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${lang === "te" ? "bg-white shadow text-[#1E3A8A]" : "text-gray-500"}`} data-testid="lang-te">తెలుగు</button>
            </div>
            <Link to="/" className="text-sm text-gray-600 hover:text-[#1E3A8A]">{t("form.cancel")}</Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {/* Step indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const active = step === s.id;
              const done = step > s.id;
              return (
                <div key={s.id} className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    done ? "bg-[#10B981] text-white" : active ? "bg-[#1E3A8A] text-white scale-110 shadow-lg" : "bg-gray-200 text-gray-500"
                  }`}>
                    {done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <div className={`text-xs mt-2 font-medium ${active ? "text-[#1E3A8A]" : "text-gray-500"}`}>{t(s.labelKey)}</div>
                </div>
              );
            })}
          </div>
          <Progress value={progress} className="h-1.5" data-testid="register-progress" />
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6 sm:p-8 animate-fade-up">
          {step === 1 && (
            <div className="space-y-5" data-testid="step-personal">
              <h2 className="font-heading font-bold text-2xl">{t("personal.heading")}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t("field.full_name")}>
                  <Input data-testid="input-full-name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder={t("field.full_name.placeholder")} />
                </Field>
                <Field label={t("field.aadhaar")}>
                  <div className="flex gap-2">
                    <Input
                      data-testid="input-aadhaar"
                      value={formatAadhaar(form.aadhaar_number)}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "").slice(0, 12);
                        update("aadhaar_number", digits);
                        if (aadhaarStatus === "verified") setAadhaarStatus("idle");
                      }}
                      placeholder={t("field.aadhaar.placeholder")}
                      maxLength={14}
                      className={aadhaarStatus === "verified" ? "border-[#10B981] bg-[#10B981]/5" : ""}
                    />
                    {aadhaarStatus === "verified" ? (
                      <div className="inline-flex items-center gap-1 px-3 rounded-md bg-[#10B981]/10 text-[#065F46] text-xs font-medium" data-testid="aadhaar-verified-badge">
                        <ShieldCheck className="w-4 h-4" /> {t("aadhaar.verified")}
                      </div>
                    ) : (
                      <Button type="button" variant="outline" onClick={sendAadhaarOtp} disabled={aadhaarStatus === "sending" || !isValidAadhaar(form.aadhaar_number) || !/^\d{10}$/.test(form.mobile)} data-testid="aadhaar-verify-btn">
                        {aadhaarStatus === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                        <span className="ml-1">{t("aadhaar.verify")}</span>
                      </Button>
                    )}
                  </div>
                  {form.aadhaar_number.length === 12 && !isValidAadhaar(form.aadhaar_number) && (
                    <div className="text-xs text-[#E11D48] mt-1">{t("aadhaar.invalid")}</div>
                  )}
                  {aadhaarStatus === "otp" && (
                    <div className="mt-2 p-3 bg-[#FEF3C7] border border-[#FCD34D] rounded-md flex gap-2 items-center">
                      <Input data-testid="otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder={t("otp.label")} maxLength={6} className="bg-white" />
                      <Button type="button" onClick={verifyAadhaarOtp} disabled={otp.length !== 6} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90" data-testid="otp-verify-btn">
                        {t("otp.verify")}
                      </Button>
                      {demoOtp && <span className="text-xs text-[#92400E] font-mono shrink-0">demo: {demoOtp}</span>}
                    </div>
                  )}
                </Field>
                <Field label={t("field.gender")}>
                  <RadioGroup value={form.gender} onValueChange={(v) => update("gender", v)} className="flex gap-4 pt-2">
                    {[
                      { v: "Male", k: "gender.male" },
                      { v: "Female", k: "gender.female" },
                      { v: "Other", k: "gender.other" },
                    ].map((g) => (
                      <div key={g.v} className="flex items-center gap-2">
                        <RadioGroupItem value={g.v} id={`g-${g.v}`} data-testid={`gender-${g.v}`} />
                        <Label htmlFor={`g-${g.v}`}>{t(g.k)}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </Field>
                <Field label={t("field.dob")}>
                  <Input data-testid="input-dob" type="date" value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} />
                </Field>
                <Field label={t("field.mobile")}>
                  <Input data-testid="input-mobile" value={form.mobile} onChange={(e) => update("mobile", e.target.value)} placeholder={t("field.mobile.placeholder")} maxLength={10} />
                </Field>
                <Field label={t("field.email")}>
                  <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} />
                </Field>
                <Field label={t("field.village")}>
                  <Select value={form.village} onValueChange={(v) => update("village", v)}>
                    <SelectTrigger data-testid="select-village"><SelectValue placeholder={t("field.village.placeholder")} /></SelectTrigger>
                    <SelectContent>
                      {villages.map((v) => <SelectItem key={v.village_id} value={v.name}>{v.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("field.pincode")}>
                  <Input data-testid="input-pincode" value={form.pincode} onChange={(e) => update("pincode", e.target.value)} maxLength={6} />
                </Field>
              </div>
              <Field label={t("field.address")}>
                <Textarea data-testid="input-address" rows={3} value={form.address} onChange={(e) => update("address", e.target.value)} />
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="step-education">
              <h2 className="font-heading font-bold text-2xl">{t("education.heading")}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Highest Education *">
                  <Select value={form.education_level} onValueChange={(v) => update("education_level", v)}>
                    <SelectTrigger data-testid="select-education-level"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {["Below 10th", "10th Pass", "12th Pass", "ITI", "Diploma", "Graduate", "Post Graduate", "Other"].map((e) => (
                        <SelectItem key={e} value={e}>{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Status *">
                  <Select value={form.education_status} onValueChange={(v) => update("education_status", v)}>
                    <SelectTrigger data-testid="select-education-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Completed", "Pursuing", "Dropped"].map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Qualification details (institution, year, marks)">
                <Textarea data-testid="input-qualification" rows={3} value={form.qualification_details} onChange={(e) => update("qualification_details", e.target.value)} placeholder="e.g. B.Tech CSE, JNTU Kakinada, 2023, 72%" />
              </Field>
              <Field label="Skills you already know">
                <div className="grid sm:grid-cols-4 gap-2 pt-1">
                  {SKILLS.map((s) => (
                    <label key={s} className={`flex items-center gap-2 p-2.5 border rounded-md cursor-pointer ${form.skills_known.includes(s) ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-gray-200"}`}>
                      <Checkbox checked={form.skills_known.includes(s)} onCheckedChange={() => toggleArr("skills_known", s)} data-testid={`skill-${s}`} />
                      <span className="text-sm">{s}</span>
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Languages known">
                <div className="flex flex-wrap gap-2 pt-1">
                  {LANGUAGES.map((s) => (
                    <label key={s} className={`flex items-center gap-2 px-3 py-2 border rounded-full cursor-pointer ${form.languages_known.includes(s) ? "border-[#F59E0B] bg-[#F59E0B]/10" : "border-gray-200"}`}>
                      <Checkbox checked={form.languages_known.includes(s)} onCheckedChange={() => toggleArr("languages_known", s)} data-testid={`lang-${s}`} />
                      <span className="text-sm">{s}</span>
                    </label>
                  ))}
                </div>
              </Field>

              {/* Existing skill + upgrade ambitions (free-text) */}
              <div className="border-t pt-4 mt-2 space-y-4">
                <Field label="Tell us about an existing skill / craft you already practise">
                  <Textarea
                    rows={3}
                    value={form.existing_skill_details}
                    onChange={(e) => update("existing_skill_details", e.target.value)}
                    placeholder="e.g. Been doing tailoring for 4 years from home. Earn ₹3,000/month stitching blouses for neighbours. Trained under my aunt."
                    data-testid="input-existing-skill"
                  />
                </Field>
                <Field label="Which specific skill do you want to upgrade to — and your experience so far?">
                  <Textarea
                    rows={3}
                    value={form.desired_upskill_with_experience}
                    onChange={(e) => update("desired_upskill_with_experience", e.target.value)}
                    placeholder="e.g. Want to learn boutique-level fashion designing with AI design tools. Currently know basic stitching. 6 months of self-practice on YouTube."
                    data-testid="input-desired-upskill"
                  />
                </Field>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="step-interests">
              <h2 className="font-heading font-bold text-2xl">{t("interests.heading")}</h2>
              <p className="text-sm text-gray-600">{t("interests.subtitle")}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {DOMAINS.map((d) => (
                  <label key={d} className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-all ${form.interested_domains.includes(d) ? "border-[#1E3A8A] bg-[#1E3A8A]/5 shadow-sm" : "border-gray-200 hover:border-gray-300"}`}>
                    <Checkbox checked={form.interested_domains.includes(d)} onCheckedChange={() => toggleArr("interested_domains", d)} data-testid={`domain-${d}`} />
                    <span className="text-sm">{d}</span>
                  </label>
                ))}
              </div>
              <div className="rounded-lg p-4 bg-[#FEF3C7] border border-[#FCD34D] flex items-start gap-3">
                <Checkbox id="ai-upskill" checked={form.ai_upskill_interest} onCheckedChange={(v) => update("ai_upskill_interest", !!v)} data-testid="ai-upskill-check" />
                <div>
                  <Label htmlFor="ai-upskill" className="font-heading font-semibold text-[#92400E] cursor-pointer">I'm interested in AI Upskilling track</Label>
                  <p className="text-xs text-[#92400E]/80 mt-0.5">Free AI/ML training designed for rural youth via RTIH.</p>
                </div>
              </div>

              {/* SSC Courses Catalog */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-heading font-bold text-base flex items-center gap-2">
                      <Brain className="w-4 h-4 text-[#1E3A8A]" /> AI Upskill Courses (NSDC SSC)
                    </h3>
                    <p className="text-xs text-gray-500">Pick specific Sector Skill Council courses you'd like to enrol in. Every course is offered with AI integration.</p>
                  </div>
                  <Link to="/courses" target="_blank" className="text-xs text-[#1E3A8A] hover:underline shrink-0">Browse full catalog →</Link>
                </div>
                <Input
                  placeholder="Search courses (e.g. 'tractor', 'tailor', 'developer')…"
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                  className="mb-3"
                  data-testid="course-filter-input"
                />
                <div className="max-h-72 overflow-y-auto pr-1 space-y-1.5 border rounded-lg p-2 bg-gray-50">
                  {filteredCourses.map((c) => (
                      <label
                        key={c.course_id}
                        className={`flex items-start gap-2 p-2.5 rounded-md cursor-pointer transition-colors ${
                          form.selected_courses.includes(c.course_id)
                            ? "bg-white border-2 border-[#1E3A8A]"
                            : "bg-white border border-gray-200 hover:border-gray-300"
                        }`}
                        data-testid={`course-pick-${c.course_code}`}
                      >
                        <Checkbox
                          checked={form.selected_courses.includes(c.course_id)}
                          onCheckedChange={() => toggleArr("selected_courses", c.course_id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{c.course_name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E] font-bold uppercase tracking-wider">AI Upskill</span>
                          </div>
                          <div className="text-xs text-gray-500">{c.ssc_code} · NSQF L{c.nsqf_level} · {c.duration_weeks}w</div>
                          <div className="text-xs text-gray-600 mt-0.5 line-clamp-1">{c.ai_integration}</div>
                        </div>
                      </label>
                    ))}
                </div>
                <div className="text-xs text-gray-500 mt-1">{form.selected_courses.length} course(s) selected</div>
              </div>

              {/* Local Handicrafts */}
              <div className="border-t pt-4">
                <h3 className="font-heading font-bold text-base mb-1">Local Handicrafts & Artisan Skills</h3>
                <p className="text-xs text-gray-500 mb-3">Andhra Pradesh's living craft traditions — tick the ones you're interested in learning or already practise.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {HANDICRAFTS.map((h) => (
                    <label
                      key={h}
                      className={`flex items-center gap-2 p-2.5 border rounded-md cursor-pointer transition-colors ${
                        form.interested_handicrafts.includes(h)
                          ? "border-[#E11D48] bg-[#E11D48]/5"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <Checkbox
                        checked={form.interested_handicrafts.includes(h)}
                        onCheckedChange={() => toggleArr("interested_handicrafts", h)}
                        data-testid={`handicraft-${h}`}
                      />
                      <span className="text-sm">{h}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Self-Employment / Skilled Trades */}
              <div className="border-t pt-4">
                <h3 className="font-heading font-bold text-base mb-1">Self-Employment & Skilled Trades</h3>
                <p className="text-xs text-gray-500 mb-3">Hands-on trades that lead to immediate self-employment income.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {SELF_EMPLOYMENT.map((s) => (
                    <label
                      key={s}
                      className={`flex items-center gap-2 p-2.5 border rounded-md cursor-pointer transition-colors ${
                        form.self_employment_interests.includes(s)
                          ? "border-[#10B981] bg-[#10B981]/5"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <Checkbox
                        checked={form.self_employment_interests.includes(s)}
                        onCheckedChange={() => toggleArr("self_employment_interests", s)}
                        data-testid={`selfemp-${s}`}
                      />
                      <span className="text-sm">{s}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5" data-testid="step-career">
              <h2 className="font-heading font-bold text-2xl">{t("career.heading")}</h2>
              <Field label="Current Employment Status *">
                <Select value={form.employment_status} onValueChange={(v) => update("employment_status", v)}>
                  <SelectTrigger data-testid="select-employment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Unemployed", "Self-employed", "Looking for change", "Part-time", "Student"].map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="What are you looking for? *">
                <RadioGroup value={form.placement_preference} onValueChange={(v) => update("placement_preference", v)} className="grid sm:grid-cols-3 gap-3 pt-2">
                  {[
                    { v: "Job", label: "Job/Placement", desc: "Find employment" },
                    { v: "Self-employment", label: "Self-employment", desc: "Start a business" },
                    { v: "Both", label: "Both", desc: "Open to either" },
                  ].map((o) => (
                    <label key={o.v} className={`p-4 border rounded-lg cursor-pointer ${form.placement_preference === o.v ? "border-[#E11D48] bg-[#E11D48]/5" : "border-gray-200"}`}>
                      <RadioGroupItem value={o.v} className="sr-only" data-testid={`pref-${o.v}`} />
                      <div className="font-heading font-semibold">{o.label}</div>
                      <div className="text-xs text-gray-500 mt-1">{o.desc}</div>
                    </label>
                  ))}
                </RadioGroup>
              </Field>
              <Field label="Job type preference">
                <Select value={form.job_type_preference} onValueChange={(v) => update("job_type_preference", v)}>
                  <SelectTrigger data-testid="select-job-type"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    {["Government", "Private", "Remote", "Local/Nearby", "Anywhere"].map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Family monthly income (₹)">
                <Select value={form.family_income} onValueChange={(v) => update("family_income", v)}>
                  <SelectTrigger data-testid="select-income"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    {["< 10,000", "10,000 - 25,000", "25,000 - 50,000", "50,000 - 1,00,000", "> 1,00,000"].map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5" data-testid="step-bank">
              <h2 className="font-heading font-bold text-2xl">{t("bank.heading")}</h2>
              <Field label="Do you have a startup / business idea?">
                <Textarea data-testid="input-startup-idea" rows={4} value={form.startup_idea} onChange={(e) => update("startup_idea", e.target.value)} placeholder="Briefly describe your idea (optional)" />
              </Field>
              <div className="rounded-lg p-4 bg-[#10B981]/10 border border-[#10B981]/30">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={form.bank_linkage_required} onCheckedChange={(v) => update("bank_linkage_required", !!v)} data-testid="bank-linkage-check" />
                  <div>
                    <div className="font-heading font-semibold text-[#065F46]">I need bank loan / linkage support (MUDRA / PMEGP / NRLM)</div>
                    <div className="text-xs text-[#065F46]/80 mt-0.5">RTIH will guide you through documents and approvals.</div>
                  </div>
                </label>
              </div>
              {form.bank_linkage_required && (
                <Field label="Estimated loan amount (₹)">
                  <Select value={form.bank_loan_amount} onValueChange={(v) => update("bank_loan_amount", v)}>
                    <SelectTrigger data-testid="select-loan-amount"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {["< 50,000", "50,000 - 1 Lakh", "1 - 5 Lakh", "5 - 10 Lakh", "> 10 Lakh"].map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              <div className="rounded-lg p-4 bg-gray-50 border border-gray-200 mt-4">
                <div className="font-heading font-semibold text-sm mb-2">Review summary</div>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>👤 {form.full_name || "—"} · {form.gender} · {form.village || "—"}</li>
                  <li>🎓 {form.education_level || "—"} ({form.education_status})</li>
                  <li>💼 Looking for: {form.placement_preference}</li>
                  <li>💡 Domains: {form.interested_domains.length} selected{form.ai_upskill_interest ? " · AI track ✓" : ""}</li>
                  <li>🎓 SSC Courses: {form.selected_courses.length} AI Upskill course(s) picked</li>
                  <li>🧶 Handicrafts: {form.interested_handicrafts.length} · 🔧 Self-employment trades: {form.self_employment_interests.length}</li>
                  <li>🏦 Bank linkage: {form.bank_linkage_required ? "Yes" : "No"}</li>
                </ul>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
            <Button variant="outline" onClick={prev} disabled={step === 1} data-testid="btn-prev">
              <ChevronLeft className="w-4 h-4 mr-1" /> {t("btn.back")}
            </Button>
            {step < 5 ? (
              <Button onClick={next} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90" data-testid="btn-next">
                {t("btn.next")} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="bg-[#10B981] hover:bg-[#10B981]/90" data-testid="btn-submit">
                {submitting ? t("btn.submitting") : t("btn.submit")} <Check className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-medium text-gray-700 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
